import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import t from "@ext/localization/locale/translate";
import { getArticlePropertyRows } from "@ext/properties/logic/getArticlePropertyRows";
import { usePropertyEditor } from "@ext/properties/logic/hooks/usePropertyEditor";
import { useReorderCatalogProperties } from "@ext/properties/logic/hooks/useReorderCatalogProperties";
import type { Property } from "@ext/properties/models";
import { isComplexProperty } from "@ext/templates/models/properties";
import {
	PanelEmptyState,
	PanelEmptyStateDescription,
	PanelEmptyStateIcon,
	PanelEmptyStateTitle,
} from "@ui-kit/FloatingPanel";
import { Icon } from "@ui-kit/Icon";
import { MenuItem } from "@ui-kit/MenuItem";
import { ScrollShadowContainer } from "@ui-kit/ScrollShadowContainer";
import { useMemo } from "react";
import { ArticlePropertyRow } from "./ArticlePropertyRow";

interface ArticlePropertiesPopoverContentProps {
	properties: Property[];
	catalogProperties: Map<string, Property>;
	onSubmit: (id: string, value?: string) => void;
	onDelete: (id: string) => void;
}

export const ArticlePropertiesPopoverContent = ({
	properties,
	catalogProperties,
	onSubmit,
	onDelete,
}: ArticlePropertiesPopoverContentProps) => {
	const openPropertyEditor = usePropertyEditor({ canAdd: true, catalogProperties, properties });
	const { isSaving, reorder } = useReorderCatalogProperties();
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	);
	const rows = useMemo(
		() =>
			getArticlePropertyRows(Array.from(catalogProperties.values()), properties).filter(
				({ property }) => !isComplexProperty[property.type],
			),
		[catalogProperties, properties],
	);
	const rowIds = useMemo(() => rows.map(({ property }) => property.id), [rows]);

	const handleDragEnd = ({ active, over }: DragEndEvent) => {
		if (!over) return;
		void reorder(String(active.id), String(over.id));
	};

	return (
		<section className="flex min-h-0 flex-1 flex-col">
			<header className="shrink-0 border-b px-4 py-3">
				<h2 className="text-sm font-semibold text-foreground">{t("properties.article-title")}</h2>
			</header>
			{rows.length > 0 ? (
				<ScrollShadowContainer
					className="min-h-0 flex-1 overflow-x-hidden px-2 py-2"
					data-testid="article-properties-scroll"
				>
					<DndContext
						collisionDetection={closestCenter}
						modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
						onDragEnd={handleDragEnd}
						sensors={sensors}
					>
						<SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
							<div className="flex flex-col gap-0.5">
								{rows.map(({ isAssigned, property }) => (
									<ArticlePropertyRow
										disabled={isSaving}
										isAssigned={isAssigned}
										key={property.id}
										onDelete={onDelete}
										onEdit={openPropertyEditor}
										onSubmit={onSubmit}
										property={property}
									/>
								))}
							</div>
						</SortableContext>
					</DndContext>
				</ScrollShadowContainer>
			) : (
				<PanelEmptyState className="pt-20">
					<PanelEmptyStateIcon icon="tag" />
					<PanelEmptyStateTitle>{t("properties.empty-state.title")}</PanelEmptyStateTitle>
					<PanelEmptyStateDescription className="max-w-64 whitespace-pre-line">
						{t("properties.empty-state.description")}
					</PanelEmptyStateDescription>
				</PanelEmptyState>
			)}
			<footer className="shrink-0 border-t p-2">
				<MenuItem
					className="min-h-8 w-full gap-2 text-sm rounded-lg bg-transparent px-2 hover:bg-secondary-bg-hover focus-visible:bg-secondary-bg-hover"
					onClick={() => openPropertyEditor(null)}
					onKeyDown={(event) => {
						if (event.key !== "Enter" && event.key !== " ") return;
						event.preventDefault();
						openPropertyEditor(null);
					}}
					role="button"
					tabIndex={0}
				>
					<Icon className="size-4" icon="plus" />
					{t("properties.add")}
				</MenuItem>
			</footer>
		</section>
	);
};
